// First-run wizard. Two steps: yard details → first horse.
// Restyled to the v6 visual language. Lives at /onboarding.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { useEffect } from "react";
import type { User } from "firebase/auth";
import { auth } from "../firebase";
import { useUserYard } from "../lib/yard";
import { createHorse } from "../lib/horses";
import { saveOnboarding } from "../lib/onboarding";
import { AVATAR_TONES } from "../lib/avatarTones";
import type { HorseColour } from "../lib/avatarTones";
import "../styles/dashboard.css";
import "../styles/add-horse.css";
import "../styles/onboarding.css";

type Step = "yard" | "horse" | "done";

const FOCUSES = [
  { k: "colic", label: "Catch colic risk early" },
  { k: "team", label: "Coordinate the team's logging" },
  { k: "performance", label: "Performance / competition prep" },
  { k: "vet", label: "Share data with my vet" },
];

const COLOURS: { k: HorseColour; label: string }[] = [
  { k: "bay", label: "Bay" },
  { k: "chestnut", label: "Chestnut" },
  { k: "grey", label: "Grey" },
  { k: "black", label: "Black" },
  { k: "palomino", label: "Palomino" },
  { k: "piebald", label: "Piebald" },
  { k: "dun", label: "Dun" },
  { k: "roan", label: "Roan" },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(auth.currentUser);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsub();
  }, []);
  const { yard } = useUserYard(user?.uid);

  const [step, setStep] = useState<Step>("yard");

  // Step 1 fields
  const [postcode, setPostcode] = useState("");
  const [focus, setFocus] = useState<string>("");
  const [saving, setSaving] = useState(false);

  // Step 2 fields
  const [horseName, setHorseName] = useState("");
  const [horseWeight, setHorseWeight] = useState("");
  const [horseColour, setHorseColour] = useState<HorseColour | "">("");

  const saveYard = async () => {
    if (!user?.uid || !yard?.id) return;
    setSaving(true);
    try {
      await saveOnboarding({
        userId: user.uid,
        yardId: yard.id,
        postcode: postcode || null,
        primaryFocus: focus || null,
      });
      setStep("horse");
    } finally {
      setSaving(false);
    }
  };

  const saveFirstHorse = async () => {
    if (!user?.uid || !yard?.id) return;
    const weightKg = +horseWeight;
    if (!horseName.trim() || !weightKg) return;
    setSaving(true);
    try {
      await createHorse(yard.id, user.uid, {
        name: horseName.trim(),
        colour: horseColour || "bay",
        weightKg,
        baselineFeed: [{ type: "Hay", kg: 6 }],
      });
      setStep("done");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="onb">
      <header className="onb-top">
        <span className="onb-brand">
          <img src="/logo-wordmark.png" alt="Troughmate" height={22} style={{ display: "block", width: "auto" }} />
        </span>
        <span className="onb-step muted mono">
          {step === "yard" && "01 / 02"}
          {step === "horse" && "02 / 02"}
          {step === "done" && "Done"}
        </span>
      </header>

      <main className="onb-page">
        <div className="onb-shell">
          {step === "yard" && (
            <>
              <h1 className="onb-h1">Welcome to {yard?.name || "your yard"}.</h1>
              <p className="onb-sub">Two quick things, then you're in.</p>

              <section className="adh-sec">
                <header className="adh-sec-head">
                  <span className="adh-sec-num mono">01</span>
                  <span className="adh-sec-title">Where's the yard?</span>
                </header>
                <div className="adh-sec-body">
                  <div className="adh-field">
                    <label className="adh-label" htmlFor="onb-postcode">UK postcode</label>
                    <input
                      id="onb-postcode"
                      className="adh-input mono"
                      placeholder="GL7 5JJ"
                      value={postcode}
                      onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                      style={{ maxWidth: 200 }}
                    />
                    <span className="adh-help">Used to auto-fill ambient temp on every daily log.</span>
                  </div>
                </div>
              </section>

              <section className="adh-sec">
                <header className="adh-sec-head">
                  <span className="adh-sec-num mono">02</span>
                  <span className="adh-sec-title">What matters most to you?</span>
                </header>
                <div className="adh-sec-body">
                  <div className="onb-focus-grid">
                    {FOCUSES.map((f) => (
                      <button
                        key={f.k}
                        type="button"
                        className={`onb-focus-tile ${focus === f.k ? "active" : ""}`}
                        onClick={() => setFocus(focus === f.k ? "" : f.k)}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
              </section>

              <div className="onb-actions">
                <button className="btn primary lg" onClick={saveYard} disabled={saving}>
                  {saving ? "Saving…" : "Continue →"}
                </button>
                <button className="btn ghost" onClick={() => setStep("horse")}>
                  Skip for now
                </button>
              </div>
            </>
          )}

          {step === "horse" && (
            <>
              <h1 className="onb-h1">Add your first horse.</h1>
              <p className="onb-sub">Just name + weight to start. You can edit everything later.</p>

              <section className="adh-sec">
                <div className="adh-sec-body">
                  <div className="adh-field">
                    <label className="adh-label" htmlFor="onb-name">Name</label>
                    <input
                      id="onb-name"
                      className="adh-input"
                      placeholder="e.g. Bella"
                      value={horseName}
                      onChange={(e) => setHorseName(e.target.value)}
                      maxLength={40}
                    />
                  </div>
                  <div className="adh-field">
                    <label className="adh-label" htmlFor="onb-weight">Weight (kg)</label>
                    <div className="adh-suffixed">
                      <input
                        id="onb-weight"
                        type="text"
                        inputMode="numeric"
                        className="adh-input adh-input-numeric"
                        placeholder="550"
                        value={horseWeight}
                        onChange={(e) => setHorseWeight(e.target.value.replace(/[^0-9.]/g, ""))}
                      />
                      <span className="adh-suffix">kg</span>
                    </div>
                    <span className="adh-help adh-help-emph">Drives the EHE hydration target.</span>
                  </div>
                  <div className="adh-field">
                    <label className="adh-label">Colour (optional)</label>
                    <div className="adh-colours" role="radiogroup" aria-label="Colour">
                      {COLOURS.map((c) => (
                        <button
                          key={c.k}
                          role="radio"
                          aria-checked={horseColour === c.k}
                          className={`adh-colour ${horseColour === c.k ? "active" : ""}`}
                          onClick={() => setHorseColour(horseColour === c.k ? "" : c.k)}
                          type="button"
                        >
                          <span className="adh-colour-swatch" style={{ background: AVATAR_TONES[c.k] }}></span>
                          <span className="adh-colour-label">{c.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              <div className="onb-actions">
                <button
                  className="btn primary lg"
                  onClick={saveFirstHorse}
                  disabled={saving || !horseName.trim() || !horseWeight}
                >
                  {saving ? "Saving…" : "Add horse & finish →"}
                </button>
                <button className="btn ghost" onClick={() => setStep("done")}>
                  Skip for now
                </button>
              </div>
            </>
          )}

          {step === "done" && (
            <div className="onb-done">
              <h1 className="onb-h1">All set.</h1>
              <p className="onb-sub">
                You can add more horses, invite teammates, and start logging daily. Welcome aboard.
              </p>
              <button className="btn primary lg" onClick={() => navigate("/dashboard")}>
                Go to dashboard →
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
